import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, BASE, getAuthHeaders } from '@/api/client'
import type { GoogleDriveAccount, GoogleDriveFile } from '@/types'

export function useGoogleDriveAccounts() {
  return useQuery<GoogleDriveAccount[]>({
    queryKey: ['google-drive-accounts'],
    queryFn: () => api.get('/google-drive/accounts'),
  })
}

export function useDisconnectDrive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) =>
      api.delete<{ ok: boolean }>(`/google-drive/accounts/${accountId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-drive-accounts'] }),
  })
}

export function useDriveFiles(accountId: string | null, folderId: string | null) {
  return useQuery<{ files: GoogleDriveFile[]; nextPageToken: string | null }>({
    queryKey: ['drive-files', accountId, folderId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (folderId) params.set('folderId', folderId)
      const qs = params.toString() ? `?${params}` : ''
      return api.get(`/google-drive/accounts/${accountId}/files${qs}`)
    },
    enabled: !!accountId,
  })
}

export function useDriveFilesLoadMore(accountId: string | null, folderId: string | null, pageToken: string | null) {
  return useQuery<{ files: GoogleDriveFile[]; nextPageToken: string | null }>({
    queryKey: ['drive-files-more', accountId, folderId, pageToken],
    queryFn: () => {
      const params = new URLSearchParams()
      if (folderId) params.set('folderId', folderId)
      if (pageToken) params.set('pageToken', pageToken)
      const qs = params.toString() ? `?${params}` : ''
      return api.get(`/google-drive/accounts/${accountId}/files${qs}`)
    },
    enabled: !!accountId && !!pageToken,
  })
}

export interface ImportProgress {
  current: number
  total: number
  filename: string
  status: 'ok' | 'error'
  imported: number
  failed: number
  error?: string
}

export interface ImportResult {
  imported: number
  failed: number
  skipped: number
}

export function importDriveFiles(
  accountId: string,
  fileIds: string[],
  fileNames: string[],
  fileMimeTypes: string[],
  targetFolderId: string,
  onProgress: (progress: ImportProgress) => void,
  onDone: (result: ImportResult) => void,
  onError: (error: string) => void,
): () => void {
  const controller = new AbortController()

  fetch(`${BASE}/google-drive/accounts/${accountId}/import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ fileIds, fileNames, fileMimeTypes, targetFolderId }),
    signal: controller.signal,
  })
    .then((res) => {
      if (!res.ok) {
        res.json().then((b) => onError(b.error || `HTTP ${res.status}`)).catch(() => onError(`HTTP ${res.status}`))
        return
      }
      const reader = res.body?.getReader()
      if (!reader) { onError('No reader'); return }

      const decoder = new TextDecoder()
      let buffer = ''

      function read(): void {
        reader!.read().then(({ done, value }) => {
          if (done) return

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          let eventType = ''
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6))
                if (eventType === 'progress') onProgress(data)
                else if (eventType === 'done') onDone(data)
                else if (eventType === 'error') onError(data.message || 'Import error')
              } catch {}
            }
          }

          read()
        }).catch((err) => {
          if (err.name !== 'AbortError') onError(err.message)
        })
      }

      read()
    })
    .catch((err) => {
      if (err.name !== 'AbortError') onError(err.message)
    })

  return () => controller.abort()
}
